import type {Maybe} from "../../bootstrap/common/types";
import {noop} from "./functions";

export const MINIMUM_HORIZONTAL_SWIPE_DISTANCE = 50;
export const MINIMUM_VERTICAL_SCROLL_DISTANCE = 30;

export class HorizontalSwipeHandler {
    private posX: number = 0;
    private posY: number = 0;
    private swipeTarget: Maybe<Element> = undefined;
    private swipeLeftCallback: (event: Event, target: Element, index: number) => void = noop;
    private swipeRightCallback: (event: Event, target: Element, index: number) => void = noop;

    public constructor(private elements: Element[]) {
    }

    public onSwipeLeft(callback: (event: Event, target: Element, index: number) => void): this {
        this.swipeLeftCallback = callback;
        return this;
    }

    public onSwipeRight(callback: (event: Event, target: Element, index: number) => void): this {
        this.swipeRightCallback = callback;
        return this;
    }

    public activate(): void {
        this.elements.forEach((e, i) => {
            e.addEventListener("touchstart", event => this.swipeStart(event));
            e.addEventListener("touchmove", event => this.swipeMove(event, i));
            e.addEventListener("touchend", _ => this.swipeEnd());
        });
    }

    private isSwipeLeft(event: Event): boolean {
        return !this.isVerticalScroll(event)
            && this.posX - this.determinePosX(event) >= MINIMUM_HORIZONTAL_SWIPE_DISTANCE;
    }

    private isSwipeRight(event: Event): boolean {
        return !this.isVerticalScroll(event)
            && this.determinePosX(event) - this.posX >= MINIMUM_HORIZONTAL_SWIPE_DISTANCE;
    }

    private swipeStart(event: Event): void {
        this.swipeTarget = event.currentTarget instanceof Element ? event.currentTarget : undefined;
        this.posX = this.determinePosX(event);
        this.posY = this.determinePosY(event);
    }

    private swipeMove(event: Event, index: number): void {
        if (!this.swipeTarget) {
            return;
        }
        if (this.isSwipeLeft(event)) {
            if (event.cancelable) {
                event.preventDefault();
            }
            this.swipeLeftCallback(event, this.swipeTarget, index);
            this.swipeTarget = undefined;
        } else if (this.isSwipeRight(event)) {
            if (event.cancelable) {
                event.preventDefault();
            }
            this.swipeRightCallback(event, this.swipeTarget, index);
            this.swipeTarget = undefined;
        }
    }

    private swipeEnd(): void {
        this.swipeTarget = undefined;
        this.posX = 0;
        this.posY = 0;
    }

    private isVerticalScroll(event: Event): boolean {
        return Math.abs(this.posY - this.determinePosY(event)) >= MINIMUM_VERTICAL_SCROLL_DISTANCE;
    }

    private determinePosX(event: Event): number {
        if (!(event instanceof TouchEvent) || event.changedTouches.length === 0) {
            return 0;
        }
        return event.changedTouches[0].clientX ?? 0;
    }

    private determinePosY(event: Event): number {
        if (!(event instanceof TouchEvent) || event.changedTouches.length === 0) {
            return 0;
        }
        return event.changedTouches[0].clientY ?? 0;
    }
}
